<template>
    <header class="cabinet-header">
        <div class="container-fluid">
            <div class="row justify-content-between">
                <div class="col-auto">
                    <h3>{{ title }}</h3>
                </div>
                <div class="col d-flex justify-content-end align-items-center header-actions">
                    <LangSwitcher @changed="langChanged" />
                    <button
                        type="button"
                        class="theme-toggle"
                        :aria-label="currentTheme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'"
                        :title="currentTheme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'"
                        @click="toggleTheme"
                    >
                        <span class="theme-toggle-icons" aria-hidden="true">
                            <i class="bi bi-sun-fill theme-icon" :class="{ active: currentTheme === 'light' }"></i>
                            <i class="bi bi-moon-fill theme-icon" :class="{ active: currentTheme === 'dark' }"></i>
                        </span>
                    </button>
                    <SystemNotifications @show-modal="showAnnouncement"/>
                    <div class="profile-menu-wrap" ref="profileMenu">
                        <button
                            type="button"
                            class="profile-nav-item"
                            :aria-expanded="profileMenuOpen"
                            :aria-label="$t('header.profile')"
                            :title="username || $t('header.profile')"
                            @click="profileMenuOpen = !profileMenuOpen"
                        >
                            <span class="profile-nav-icon" aria-hidden="true">
                                <i class="bi bi-person-fill"></i>
                            </span>
                            <span class="profile-nav-details">
                                <span class="header-username text-truncate to-blur">
                                    {{ username || $t('header.default-user-tooltip') }}
                                </span>
                                <span v-if="accountIdentifier" class="profile-nav-identifier to-blur">
                                    {{ accountIdentifierLabel }}: {{ accountIdentifier }}
                                </span>
                            </span>
                            <i class="bi bi-chevron-down profile-nav-chevron" aria-hidden="true"></i>
                        </button>
                        <div v-if="profileMenuOpen" class="profile-dropdown" role="menu">
                            <div class="profile-dropdown-identity" role="group" :aria-label="username">
                                <strong class="profile-dropdown-username">{{ username || $t('header.default-user-tooltip') }}</strong>
                                <span
                                    v-for="identifier in accountIdentifiers"
                                    :key="identifier.label"
                                    class="profile-dropdown-identifier"
                                >
                                    {{ identifier.label }}: {{ identifier.value }}
                                </span>
                            </div>
                            <router-link to="/settings/profile" class="profile-dropdown-item" role="menuitem" @click="closeProfileMenu">
                                <i class="bi bi-person-vcard" aria-hidden="true"></i>
                                {{ $t('header.profile') }}
                            </router-link>
                            <router-link to="/settings/rates" class="profile-dropdown-item profile-dropdown-balance" role="menuitem" @click="closeProfileMenu">
                                <i class="bi bi-wallet2" aria-hidden="true"></i>
                                <span class="profile-dropdown-balance-content">
                                    <span class="profile-dropdown-balance-main">
                                        <span>{{ $t('header.balance') }}</span>
                                        <strong>{{ balance.toLocaleString('kk-KZ') }} ₸</strong>
                                    </span>
                                    <span class="profile-dropdown-bonus">
                                        <span>{{ $t('rates.current-bonus-balance') }}</span>
                                        <strong>{{ bonusBalance.toLocaleString('kk-KZ') }} ₸</strong>
                                    </span>
                                </span>
                            </router-link>
                            <button type="button" class="profile-dropdown-item profile-dropdown-logout" role="menuitem" @click="logout">
                                <i class="bi bi-box-arrow-right" aria-hidden="true"></i>
                                {{ $t('header.logout') }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </header>
</template>

<script>

import LangSwitcher from '../LangSwitcher.vue'
import SystemNotifications from './SystemNotifications.vue'
import { getResolvedTheme, setTheme } from '../../theme'
import { getUserDisplayName } from '../../../libs/userDisplayName'

export default {
    name: 'Navbar',
    data: () => ({
        balance: 0,
        bonusBalance: 0,
        currentTheme: 'light',
        profileMenuOpen: false
    }),
    computed: {
        username() {
            return getUserDisplayName(this.$store.state.user)
        },
        accountIdentifier() {
            return this.accountIdentifiers[0]?.value || ''
        },
        accountIdentifiers() {
            const settings = this.$store.state.user?.settings || {}
            const identifiers = []

            if(settings.bin) identifiers.push({ label: this.$t('header.bin'), value: settings.bin })
            if(settings.iinBin) identifiers.push({ label: this.$t('header.iin'), value: settings.iinBin })

            return identifiers
        },
        accountIdentifierLabel() {
            const settings = this.$store.state.user?.settings || {}
            return settings.bin ? this.$t('header.bin') : this.$t('header.iin')
        }
    },
    props: {
        title: {
            type: String,
            default: ''
        }
    },
    components: {
        LangSwitcher,
        SystemNotifications
    },
    mounted() {
        const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]')
        tooltipTriggerList.forEach(tooltipTriggerEl => new bootstrap.Tooltip(tooltipTriggerEl))
        
        this.getBalance()
        this.getBonusBalance()
        this.currentTheme = getResolvedTheme()
        window.addEventListener('kdp-theme-change', this.themeChanged)
        document.addEventListener('click', this.handleDocumentClick)
    },
    beforeUnmount() {
        window.removeEventListener('kdp-theme-change', this.themeChanged)
        document.removeEventListener('click', this.handleDocumentClick)
    },
    methods: {
        async getBalance() {
            let url = process.env.VUE_APP_API_URL + '/accounts/my'

            await axios.get(url, {
                withCredentials: true
            }).then((response) => {
                let r = response.data

                if(r.result === 'SUCCESS') {
                    this.balance = r.data.balance !== null ? r.data.balance.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ') : 0
                }
            })

            this.$emit('balanceChanged', this.balance)
        },
        async getBonusBalance() {
            let url = process.env.VUE_APP_API_URL + '/bonus-accounts/my'

            await axios.get(url, {
                withCredentials: true
            }).then((response) => {
                let r = response.data

                if(r.result === 'SUCCESS') {
                    this.bonusBalance = r.data.balance !== null ? r.data.balance.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ') : 0
                }
            })
        },
        langChanged(lang) {
            this.$emit('langChanged', lang)
        },
        toggleTheme() {
            setTheme(this.currentTheme === 'dark' ? 'light' : 'dark')
        },
        themeChanged(event) {
            this.currentTheme = getResolvedTheme(event.detail)
        },
        closeProfileMenu() {
            this.profileMenuOpen = false
        },
        handleDocumentClick(event) {
            if (this.$refs.profileMenu && !this.$refs.profileMenu.contains(event.target)) {
                this.closeProfileMenu()
            }
        },
        logout() {
            this.closeProfileMenu()
            this.$store.commit('clearClaim')

            const url = process.env.VUE_APP_API_URL + '/logout'
            axios.get(url, {
                headers: { 'Content-Type': 'application/json' },
                withCredentials: true
            }).then((response) => {
                if (response.data.result === 'SUCCESS') {
                    this.$store.commit('logout')
                    this.$router.push('/login/main')
                }
            })
        }
    }
}

</script>

<style scoped>

    .bonus-coin{
        width: 1rem; /* Set width */
        height: 1rem; /* Set height */
        margin-top: -4px
    }

    .cabinet-header {
        position: fixed;
        top: 0;
        left: 280px;
        right: 0;
        width: calc(100% - 280px);
        padding: 1em 2em;
        background-color: var(--kdp-nav-bg);
        box-shadow: .125rem 0 .75rem var(--kdp-shadow-color);
        z-index: 3;
    }

    .header-welcome {
        margin: 0 .5em 0 2em;
    }

    .header-actions {
        gap: 1rem;
    }
    
    .header-username {
        position: relative;
        display: inline-block;
        width: fit-content;
        max-width: 120px;
        overflow: hidden;
    }

    .lang-switcher {
        color: var(--kdp-nav-text);
    }

    .lang-item {
        border-left: 1px solid var(--kdp-border-default);
    }

    .nav-item {
        display: flex;
        align-items: center;
        color: var(--kdp-nav-text);
        text-decoration: none;
    }

    .profile-nav-item {
        display: inline-flex;
        align-items: center;
        min-width: 11rem;
        max-width: 17rem;
        gap: .65rem;
        padding: .3rem .75rem .3rem .35rem;
        color: var(--kdp-nav-text);
        background: transparent;
        text-decoration: none;
        border: 1px solid transparent;
        border-radius: var(--kdp-radius-md-plus);
        transition: color .18s ease, background-color .18s ease, border-color .18s ease;
    }

    .profile-menu-wrap {
        position: relative;
    }

    .profile-nav-item {
        cursor: pointer;
        font: inherit;
    }

    .profile-nav-item:hover,
    .profile-nav-item:focus-visible,
    .profile-nav-item[aria-expanded='true'] {
        color: var(--kdp-primary-link);
        background-color: var(--kdp-surface-hover);
        border-color: var(--kdp-border-default);
    }

    .profile-nav-icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        flex: 0 0 2.65rem;
        width: 2.65rem;
        height: 2.65rem;
        color: var(--kdp-text-on-primary);
        font-size: 1.35rem;
        background-color: var(--kdp-primary-strong);
        border-radius: 50%;
    }

    .profile-nav-details {
        display: flex;
        min-width: 0;
        flex-direction: column;
        line-height: 1.2;
    }

    .profile-nav-identifier {
        overflow: hidden;
        color: var(--kdp-text-muted);
        font-size: .72rem;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .balance-nav-item {
        padding: .45rem .75rem;
        white-space: nowrap;
        border-left: 1px solid var(--kdp-border-default);
    }

    .profile-nav-chevron {
        margin-left: .15rem;
        color: var(--kdp-text-muted);
        font-size: .75rem;
    }

    .profile-dropdown {
        position: absolute;
        top: calc(100% + .65rem);
        right: 0;
        z-index: 10;
        width: min(24rem, calc(100vw - 2rem));
        min-width: 18rem;
        max-width: calc(100vw - 2rem);
        padding: .4rem;
        color: var(--kdp-text-main);
        background: var(--kdp-dropdown-bg);
        border: 1px solid var(--kdp-border-default);
        border-radius: var(--kdp-radius-md-plus);
        box-shadow: var(--kdp-shadow-card);
    }

    .profile-dropdown-identity {
        display: flex;
        flex-direction: column;
        gap: .25rem;
        margin: .2rem .25rem .4rem;
        padding: .65rem .75rem .8rem;
        border-bottom: 1px solid var(--kdp-border-default);
    }

    .profile-dropdown-username {
        color: var(--kdp-text-primary);
        font-size: 1rem;
        line-height: 1.3;
        overflow-wrap: anywhere;
    }

    .profile-dropdown-identifier {
        color: var(--kdp-text-secondary);
        font-size: .78rem;
        line-height: 1.25;
        overflow-wrap: anywhere;
    }

    .profile-dropdown-item {
        display: flex;
        align-items: center;
        width: 100%;
        gap: .65rem;
        padding: .65rem .75rem;
        color: var(--kdp-text-main);
        text-align: left;
        text-decoration: none;
        background: transparent;
        border: 0;
        border-radius: var(--kdp-radius-sm);
        cursor: pointer;
    }

    .profile-dropdown-item:hover,
    .profile-dropdown-item:focus-visible {
        color: var(--kdp-primary-link);
        background: var(--kdp-dropdown-hover);
        outline: none;
    }

    .profile-dropdown-item i {
        width: 1rem;
        color: var(--kdp-primary-link);
        text-align: center;
    }

    .profile-dropdown-balance strong {
        font-size: .85rem;
        font-weight: 600;
    }

    .profile-dropdown-balance-content {
        display: flex;
        flex: 1 1 auto;
        min-width: 0;
        flex-direction: column;
        gap: .3rem;
    }

    .profile-dropdown-balance-main,
    .profile-dropdown-bonus {
        display: flex;
        align-items: center;
        min-width: 0;
        gap: .5rem;
    }

    .profile-dropdown-balance-main strong,
    .profile-dropdown-bonus strong {
        margin-left: auto;
        white-space: nowrap;
    }

    .profile-dropdown-bonus {
        color: var(--kdp-text-secondary);
        font-size: .78rem;
    }

    .profile-dropdown-bonus .bonus-coin {
        flex: 0 0 1rem;
    }

    .profile-dropdown-logout {
        margin-top: .35rem;
        border-top: 1px solid var(--kdp-border-default);
        border-radius: 0 0 var(--kdp-radius-sm) var(--kdp-radius-sm);
    }

    .profile-dropdown-logout i {
        color: var(--kdp-status-danger);
    }

    .theme-toggle {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 4.5rem;
        height: 2.35rem;
        margin-right: .5rem;
        padding: 3px;
        color: var(--kdp-text-main);
        background: var(--kdp-surface-inset);
        border: 1px solid var(--kdp-border-default);
        border-radius: var(--kdp-radius-pill);
        transition: color .18s ease, background-color .18s ease, border-color .18s ease;
    }

    .theme-toggle:hover {
        color: var(--kdp-primary-link);
        background: var(--kdp-surface-hover);
        border-color: var(--kdp-primary-accent);
    }

    .theme-toggle-icons {
        display: inline-flex;
        align-items: center;
        gap: 4px;
    }

    .theme-toggle .theme-icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 1.8rem;
        height: 1.75rem;
        color: var(--kdp-text-muted);
        font-size: .85rem;
        opacity: .55;
        border: 1px solid transparent;
        border-radius: var(--kdp-radius-pill);
        transition: color .18s ease, opacity .18s ease, background-color .18s ease, border-color .18s ease, transform .18s ease;
    }

    .theme-toggle .theme-icon.active {
        color: var(--kdp-text-on-primary);
        background: var(--kdp-primary-strong);
        border-color: var(--kdp-primary-strong);
        opacity: 1;
        transform: scale(1.08);
    }

    [data-theme='dark'] .theme-toggle .theme-icon.active {
        color: var(--kdp-primary-deep);
        background: var(--kdp-primary-soft);
        border-color: var(--kdp-primary-soft);
    }

    @media (max-width: 1199px) {
        .profile-nav-item {
            min-width: auto;
            padding-right: .5rem;
        }

        .profile-nav-details {
            display: none;
        }
    }

    @media (max-width: 1099px) {
        .balance-nav-item {
            display: none;
        }
    }

    .nav-item:hover {
        color: var(--kdp-primary-dark);
        transition: .25s;
        -webkit-transition: .25s;
        -o-transition: .25s;
        -moz-transition: .25s;
    }

    @media (max-width: 991px) {
        .cabinet-header {
            display: none;
        }
    }

</style>
